import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './PropertiesPanelComponent.css';
import { usePropertiesPanelStore } from '../../store/propertiesPanelStore';
import { useSelectionStore } from '../../store/selectionStore';
import { useDocumentsStateStore } from '../../store/documentsStateStore';
import ISceneObject from '../../model/sceneObject/ISceneObject';
import PropertyItem from '../../model/properties/PropertyItem';
import PropertyItemTypeEnum from '../../model/properties/PropertyItemTypeEnum';
import { getPropertyCategory, CATEGORY_COMMON } from '../../model/properties/PropertyCategoryRegistry';
import { getPropertyNameKey } from '../../model/properties/PropertyNames';
import { queryProperties, editProperty } from '../../logic/properties/PropertiesService';
import CommandIdEnum from '../../enums/CommandIdEnums';
import hasRequiredRights from '../../commands/commandUserRights';
import { getSceneObjectSummary } from '../../utils/sceneObjectSummary';
import { ChevronDownIcon, CloseIcon } from '../Icons';

// Placeholder until the real Color property (PID + translator + SceneObjectData field) exists.
// Selection is local UI state only, never persisted or applied to the scene objects.
const COLOR_SWATCHES = [
    '#CF7A2C', '#1D1F20', '#F2F2F3', '#8D9196',
    '#5980A6', '#1D2B28', '#8A9668', '#B0533A',
];
// Hidden for now until the real Color property is implemented; flip to true to bring the mock back.
const SHOW_COLOR_PICKER = false;

const PropertiesPanelComponent = () => {
    const { t } = useTranslation();
    const isOpen = usePropertiesPanelStore(state => state.isOpen);
    const close = usePropertiesPanelStore(state => state.close);
    const selectedSceneObjectUuids = useSelectionStore(state => state.selectedSceneObjectUuids);

    const [sceneObjects, setSceneObjects] = useState<ISceneObject[]>([]);
    const [items, setItems] = useState<PropertyItem<any>[]>([]);
    const [editingValues, setEditingValues] = useState<Record<string, string>>({});
    const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
    const [readOnly, setReadOnly] = useState<boolean>(false);
    const [colorIndex, setColorIndex] = useState<number | null>(null);

    // Movable panel: null means "use the default CSS position" (just above
    // SelectionContextToolbarComponent); once the user drags the header, this holds an explicit
    // fixed pixel position that overrides it. Kept as plain state (not the store) — resets to the
    // default position only on a full page reload, not on close/reopen, which matches how a
    // draggable window/panel is normally expected to behave.
    const panelRef = useRef<HTMLDivElement>(null);
    const dragState = useRef<{ startX: number; startY: number; startTop: number; startLeft: number } | null>(null);
    const [dragPosition, setDragPosition] = useState<{ top: number; left: number } | null>(null);

    const handleHeaderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        // Don't start a drag when the press originated on the close button itself
        if ((e.target as HTMLElement).closest('.properties-panel-close')) return;

        const rect = panelRef.current?.getBoundingClientRect();
        if (!rect) return;

        dragState.current = { startX: e.clientX, startY: e.clientY, startTop: rect.top, startLeft: rect.left };
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const handleHeaderPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragState.current) return;

        const { startX, startY, startTop, startLeft } = dragState.current;
        const panelWidth = panelRef.current?.offsetWidth ?? 0;
        const panelHeight = panelRef.current?.offsetHeight ?? 0;

        // Keep at least a sliver of the panel on-screen regardless of where the drag ends
        const maxLeft = Math.max(0, window.innerWidth - panelWidth);
        const maxTop = Math.max(0, window.innerHeight - panelHeight);

        const nextLeft = Math.min(maxLeft, Math.max(0, startLeft + (e.clientX - startX)));
        const nextTop = Math.min(maxTop, Math.max(0, startTop + (e.clientY - startY)));

        setDragPosition({ top: nextTop, left: nextLeft });
    };

    const handleHeaderPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        dragState.current = null;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
    };

    const panelStyle: React.CSSProperties | undefined = dragPosition
        ? { top: `${dragPosition.top}px`, left: `${dragPosition.left}px`, bottom: 'auto', transform: 'none' }
        : undefined;

    const refresh = useCallback(() => {
        const activeDocument = useDocumentsStateStore.getState().getActiveDocument();
        const resolved = selectedSceneObjectUuids
            .map(uuid => activeDocument?.document.getSceneObject(uuid))
            .filter((sceneObject): sceneObject is ISceneObject => sceneObject?.hasSelectionContextToolbar() ?? false);

        // Only reachable through the selection toolbar, so follow the same opt-out: selecting e.g.
        // a checklist item while the panel is open closes it rather than showing that item.
        if (resolved.length === 0) {
            close();
            return;
        }

        const documentReadOnly = activeDocument
            ? !hasRequiredRights(activeDocument.document.userRight, CommandIdEnum.EditProperties)
            : true;

        const propertyList = queryProperties(resolved);
        const propertyItems = propertyList.getItems();
        if (documentReadOnly) {
            propertyItems.forEach(item => { item.readOnly = true; });
        }

        const values: Record<string, string> = {};
        for (const item of propertyItems) {
            values[item.id] = item.getDisplayValue();
        }

        setSceneObjects(resolved);
        setItems(propertyItems);
        setEditingValues(values);
        setReadOnly(documentReadOnly);
        // Hardcoded color mock: single selection starts on the first swatch, multi-selection
        // starts "mixed" (no swatch selected) so the dashed/varies treatment is visible.
        setColorIndex(resolved.length === 1 ? 0 : null);
    }, [selectedSceneObjectUuids, close]);

    useEffect(() => {
        if (isOpen) refresh();
    }, [isOpen, refresh]);

    // The panel is non-modal (no backdrop), so the selection can change underneath it; the
    // refresh above follows it, and once nothing is selected there's nothing left to show.
    useEffect(() => {
        if (isOpen && selectedSceneObjectUuids.length === 0) close();
    }, [isOpen, selectedSceneObjectUuids, close]);

    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOpen, close]);

    if (!isOpen) {
        return null;
    }

    const handleCommit = async (item: PropertyItem<any>) => {
        if (readOnly) return;

        const text = editingValues[item.id];
        if (!item.varying && text === item.getDisplayValue()) return; // unchanged, nothing to persist

        const ok = await editProperty(sceneObjects, item, text);
        if (ok) {
            refresh();
        } else {
            // Revert to the item's last valid display value on parse/validation failure.
            setEditingValues(prev => ({ ...prev, [item.id]: item.varying ? '' : item.getDisplayValue() }));
        }
    };

    const revertField = (item: PropertyItem<any>) => {
        setEditingValues(prev => ({ ...prev, [item.id]: item.varying ? '' : item.getDisplayValue() }));
    };

    // Checkboxes commit immediately on toggle rather than on blur like text fields — bypasses
    // editingValues/handleCommit (which reads editingValues from the outer closure, so it can't
    // safely be called with a value from the same setEditingValues batch that triggered it).
    const handleBooleanToggle = async (item: PropertyItem<any>, checked: boolean) => {
        if (readOnly || item.readOnly) return;

        const ok = await editProperty(sceneObjects, item, checked ? 'true' : 'false');
        if (ok) refresh();
    };

    const handleFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, item: PropertyItem<any>) => {
        if (e.key === 'Escape') {
            revertField(item);
            (e.target as HTMLElement).blur();
            e.stopPropagation();
        } else if (e.key === 'Enter' && (e.currentTarget.tagName === 'INPUT' || e.ctrlKey || e.metaKey)) {
            (e.target as HTMLElement).blur();
        }
    };

    const categoryIds = new Set(items.map(item => item.categoryId));
    // The hardcoded Color mock always lives in the common/General category alongside the real
    // properties, so make sure that category renders even if the selection has no other properties.
    if (SHOW_COLOR_PICKER) categoryIds.add(CATEGORY_COMMON.id);

    const categories = Array.from(categoryIds)
        .map(categoryId => getPropertyCategory(categoryId))
        .filter((category): category is NonNullable<typeof category> => category != null)
        .sort((a, b) => a.sortOrder - b.sortOrder);

    const toggleCategory = (categoryId: string) => {
        setCollapsedCategories(prev => {
            const next = new Set(prev);
            if (next.has(categoryId)) next.delete(categoryId);
            else next.add(categoryId);
            return next;
        });
    };

    const summaries = sceneObjects.map(getSceneObjectSummary);
    const distinctTypeKeys = Array.from(new Set(summaries.map(summary => summary.labelKey)));
    let headerSubtitle = '';
    if (sceneObjects.length === 1) {
        headerSubtitle = t(distinctTypeKeys[0]);
    } else if (sceneObjects.length > 1 && distinctTypeKeys.length === 1) {
        headerSubtitle = t('ui.propertiesPanel.selectionCount', { count: sceneObjects.length, type: t(distinctTypeKeys[0]) });
    } else if (sceneObjects.length > 1) {
        headerSubtitle = t('ui.propertiesPanel.elementsCount', { count: sceneObjects.length });
    }
    // Header dot takes the object's color when the whole selection is of one type; otherwise the
    // CSS default (accent) applies.
    const headerDotStyle = distinctTypeKeys.length === 1 ? { backgroundColor: summaries[0].color } : undefined;

    const colorIsMixed = SHOW_COLOR_PICKER && sceneObjects.length > 1 && colorIndex === null;
    const hasMixedField = items.some(item => item.varying) || colorIsMixed;

    const handleColorSelect = (index: number) => {
        if (readOnly) return;
        setColorIndex(index);
    };

    const handleSwatchKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            const next = (index + 1) % COLOR_SWATCHES.length;
            handleColorSelect(next);
            (e.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            const next = (index - 1 + COLOR_SWATCHES.length) % COLOR_SWATCHES.length;
            handleColorSelect(next);
            (e.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
        }
    };

    return (
        <div className="properties-panel" ref={panelRef} style={panelStyle} role="dialog" aria-label={t('ui.propertiesPanel.title')}>
            <div
                className="properties-panel-header"
                onPointerDown={handleHeaderPointerDown}
                onPointerMove={handleHeaderPointerMove}
                onPointerUp={handleHeaderPointerUp}
            >
                <div className="properties-panel-header-left">
                    <span className="properties-panel-status-dot" style={headerDotStyle} />
                    <span className="properties-panel-title">{t('ui.propertiesPanel.title')}</span>
                    {headerSubtitle && <span className="properties-panel-subtitle">{headerSubtitle}</span>}
                    {readOnly && (
                        <span className="properties-panel-readonly-badge">{t('ui.propertiesPanel.readOnly')}</span>
                    )}
                </div>
                <button className="properties-panel-close" onClick={close} aria-label={t('common.close')}>
                    <CloseIcon />
                </button>
            </div>

            <div className="properties-panel-body">
                {categories.map(category => {
                    const collapsed = collapsedCategories.has(category.id);
                    const isCommonCategory = category.id === CATEGORY_COMMON.id;
                    return (
                        <div className="properties-panel-category" key={category.id}>
                            <button
                                type="button"
                                className="properties-panel-category-title"
                                aria-expanded={!collapsed}
                                onClick={() => toggleCategory(category.id)}
                            >
                                <ChevronDownIcon />
                                {t(category.name)}
                            </button>
                            {!collapsed && (
                                <div className="properties-panel-category-rows">
                                    {items
                                        .filter(item => item.categoryId === category.id)
                                        .sort((a, b) => a.sortOrder - b.sortOrder)
                                        .map(item => {
                                            const variesDescId = `${item.id}-varies-desc`;
                                            return (
                                                <div className="properties-panel-row" key={item.id}>
                                                    <div className="properties-panel-row-label">
                                                        <span>{t(getPropertyNameKey(item.id))}</span>
                                                        {item.varying && (
                                                            <span className="properties-panel-varies-chip">{t('ui.propertiesPanel.varies')}</span>
                                                        )}
                                                    </div>
                                                    <div className="properties-panel-row-value">
                                                        {item.varying && (
                                                            <span id={variesDescId} className="properties-panel-visually-hidden">
                                                                {t('ui.propertiesPanel.variesDescription', { count: sceneObjects.length })}
                                                            </span>
                                                        )}
                                                        {item.getItemType() === PropertyItemTypeEnum.MultilineString ? (
                                                            <textarea
                                                                className={`properties-panel-textarea${item.varying ? ' is-mixed' : ''}`}
                                                                rows={3}
                                                                value={item.varying ? '' : (editingValues[item.id] ?? '')}
                                                                placeholder={item.varying ? t('ui.propertiesPanel.multipleValues') : undefined}
                                                                readOnly={item.readOnly}
                                                                aria-describedby={item.varying ? variesDescId : undefined}
                                                                onChange={(e) => setEditingValues(prev => ({ ...prev, [item.id]: e.target.value }))}
                                                                onBlur={() => handleCommit(item)}
                                                                onKeyDown={(e) => handleFieldKeyDown(e, item)}
                                                            />
                                                        ) : item.getItemType() === PropertyItemTypeEnum.Boolean ? (
                                                            <div className="properties-panel-checkbox-cell">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!item.varying && (editingValues[item.id] === 'true')}
                                                                    ref={(el) => { if (el) el.indeterminate = item.varying; }}
                                                                    disabled={item.readOnly}
                                                                    aria-label={t(getPropertyNameKey(item.id))}
                                                                    aria-describedby={item.varying ? variesDescId : undefined}
                                                                    onChange={(e) => handleBooleanToggle(item, e.target.checked)}
                                                                />
                                                            </div>
                                                        ) : item.getItemType() === PropertyItemTypeEnum.Length ? (
                                                            <div className={`properties-panel-length-field${item.varying ? ' is-mixed' : ''}`}>
                                                                <input
                                                                    type="text"
                                                                    value={item.varying ? '' : (editingValues[item.id] ?? '')}
                                                                    placeholder={item.varying ? t('ui.propertiesPanel.multipleValues') : undefined}
                                                                    readOnly={item.readOnly}
                                                                    aria-describedby={item.varying ? variesDescId : undefined}
                                                                    onChange={(e) => setEditingValues(prev => ({ ...prev, [item.id]: e.target.value }))}
                                                                    onBlur={() => handleCommit(item)}
                                                                    onKeyDown={(e) => handleFieldKeyDown(e, item)}
                                                                />
                                                                <span className="properties-panel-length-unit">cm</span>
                                                            </div>
                                                        ) : (
                                                            <input
                                                                className={item.varying ? 'is-mixed' : ''}
                                                                type="text"
                                                                value={item.varying ? '' : (editingValues[item.id] ?? '')}
                                                                placeholder={item.varying ? t('ui.propertiesPanel.multipleValues') : undefined}
                                                                readOnly={item.readOnly}
                                                                aria-describedby={item.varying ? variesDescId : undefined}
                                                                onChange={(e) => setEditingValues(prev => ({ ...prev, [item.id]: e.target.value }))}
                                                                onBlur={() => handleCommit(item)}
                                                                onKeyDown={(e) => handleFieldKeyDown(e, item)}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}

                                    {SHOW_COLOR_PICKER && isCommonCategory && (
                                        <div className="properties-panel-row stacked">
                                            <div className="properties-panel-row-label">
                                                <span>{t('properties.field.color.name')}</span>
                                                {colorIsMixed && (
                                                    <span className="properties-panel-varies-chip">{t('ui.propertiesPanel.varies')}</span>
                                                )}
                                            </div>
                                            <div className="properties-panel-row-value">
                                                {colorIsMixed && (
                                                    <span id="color-varies-desc" className="properties-panel-visually-hidden">
                                                        {t('ui.propertiesPanel.variesDescription', { count: sceneObjects.length })}
                                                    </span>
                                                )}
                                                <div
                                                    className={`properties-panel-color-swatches${colorIsMixed ? ' is-mixed' : ''}`}
                                                    role="radiogroup"
                                                    aria-label={t('properties.field.color.name')}
                                                    aria-describedby={colorIsMixed ? 'color-varies-desc' : undefined}
                                                >
                                                    {COLOR_SWATCHES.map((hex, index) => {
                                                        const selected = colorIndex === index;
                                                        return (
                                                            <button
                                                                key={hex}
                                                                type="button"
                                                                role="radio"
                                                                aria-checked={selected}
                                                                aria-label={hex}
                                                                title={hex}
                                                                tabIndex={selected || (colorIndex === null && index === 0) ? 0 : -1}
                                                                className={`properties-panel-color-swatch${selected ? ' selected' : ''}`}
                                                                style={{ backgroundColor: hex }}
                                                                disabled={readOnly}
                                                                onClick={() => handleColorSelect(index)}
                                                                onKeyDown={(e) => handleSwatchKeyDown(e, index)}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {hasMixedField && (
                    <div className="properties-panel-footer-note">
                        <span className="properties-panel-footer-marker">!</span>
                        <span>{t('ui.propertiesPanel.mixedNote', { count: sceneObjects.length })}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PropertiesPanelComponent;
